import { Box, Container, Grid, Typography, Card, CardContent, Stack } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HandshakeIcon from '@mui/icons-material/Handshake';
import VerifiedIcon from '@mui/icons-material/Verified';
import PageHero from '../components/common/PageHero';
import SectionHeader from '../components/common/SectionHeader';
import AnimatedSection from '../components/common/AnimatedSection';
import CTABanner from '../components/common/CTABanner';
import { usePageContent } from '../hooks/usePublicApi';
import { getImageUrl } from '../utils/imageUrl';

const iconMap = {
  Verified: VerifiedIcon,
  Handshake: HandshakeIcon,
  Groups: GroupsIcon,
  EmojiEvents: EmojiEventsIcon,
};

const defaultValues = [
  { icon: VerifiedIcon, title: 'Qualität', description: 'Höchste Qualitätsstandards in jedem Projekt. Wir arbeiten sorgfältig, präzise und mit den besten Materialien.' },
  { icon: HandshakeIcon, title: 'Zuverlässigkeit', description: 'Termintreue und transparente Kommunikation. Wir halten, was wir versprechen — immer.' },
  { icon: GroupsIcon, title: 'Teamwork', description: 'Starkes Team mit erfahrenen Fachkräften. Gemeinsam erreichen wir die besten Ergebnisse.' },
  { icon: EmojiEventsIcon, title: 'Erfahrung', description: 'Über 15 Jahre Erfahrung im Innenausbau. Hunderte zufriedene Kunden in München und Umgebung.' },
];

const defaultMilestones = [
  { year: '2009', title: 'Gründung', desc: 'Gründung der Süd Ausbau GmbH in München.' },
  { year: '2012', title: 'Wachstum', desc: 'Erweiterung des Teams und des Leistungsspektrums.' },
  { year: '2016', title: 'Großprojekte', desc: 'Erste Großprojekte im gewerblichen Innenausbau.' },
  { year: '2020', title: 'Expansion', desc: 'Erweiterung in den Großraum München und Oberbayern.' },
  { year: '2024', title: 'Heute', desc: 'Über 30 Mitarbeiter und 150+ realisierte Projekte.' },
];

function resolveValueIcon(value) {
  if (typeof value.icon === 'function') return value.icon;
  if (typeof value.icon === 'string') return iconMap[value.icon] || VerifiedIcon;
  if (typeof value.icon_name === 'string') return iconMap[value.icon_name] || VerifiedIcon;
  return VerifiedIcon;
}

export default function UeberUns() {
  const { content: aboutContent } = usePageContent('about');
  const heroContent = aboutContent?.hero;
  const heroBackgroundImage = heroContent?.backgroundImage
    ? getImageUrl(heroContent.backgroundImage.filename, heroContent.backgroundImage.folder)
    : null;
  const introContent = aboutContent?.intro;
  const introParagraphs = Array.isArray(introContent?.paragraphs)
    ? introContent.paragraphs
    : [
      'Die Süd Ausbau GmbH ist ein inhabergeführtes Unternehmen mit Sitz in München. Seit unserer Gründung im Jahr 2009 haben wir uns auf hochwertigen Innenausbau spezialisiert — für private und gewerbliche Kunden.',
      'Unser Team aus erfahrenen Fachkräften verbindet traditionelles Handwerk mit modernen Techniken und Materialien. Wir begleiten Sie von der ersten Idee bis zur schlüsselfertigen Übergabe — zuverlässig, termingerecht und immer mit dem Anspruch, das Beste aus Ihren Räumen herauszuholen.',
    ];
  const valuesData = aboutContent?.values?.items || aboutContent?.values;
  const values = Array.isArray(valuesData)
    ? valuesData.map(v => ({ ...v, icon: resolveValueIcon(v) }))
    : defaultValues;
  const milestones = aboutContent?.milestones?.items || defaultMilestones;
  return (
    <>
      <Helmet>
        <title>Über uns — Süd Ausbau GmbH</title>
        <meta name="description" content="Erfahren Sie mehr über die Süd Ausbau GmbH — Ihr erfahrener Partner für Innenausbau, Umbau und Sanierung in München seit 2009." />
        <link rel="canonical" href="https://sued-ausbau.de/uber-uns" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sued-ausbau.de/uber-uns" />
        <meta property="og:title" content="Über uns — Süd Ausbau GmbH" />
        <meta property="og:description" content="Erfahren Sie mehr über die Süd Ausbau GmbH — Ihr erfahrener Partner für Innenausbau, Umbau und Sanierung in München seit 2009." />
        <meta property="og:image" content="https://sued-ausbau.de/og-preview.png" />
      </Helmet>

      <PageHero
        overline={heroContent?.subtitle || 'Über uns'}
        title={heroContent?.title || 'Ihr Partner für Innenausbau'}
        description={heroContent?.description}
        backgroundImage={heroBackgroundImage}
      />

      {/* Intro */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <AnimatedSection>
            <Card sx={{ p: { xs: 2, md: 4 } }}>
              <CardContent>
                <Typography variant="h3" sx={{ mb: 3, textAlign: 'center' }}>
                  {introContent?.title || 'Wer wir sind'}
                </Typography>
                {introParagraphs.map((para, i) => (
                  <Typography key={i} variant="body1" color="text.secondary" sx={{ mb: i < introParagraphs.length - 1 ? 2 : 0, lineHeight: 1.8 }}>
                    {para}
                  </Typography>
                ))}
              </CardContent>
            </Card>
          </AnimatedSection>
        </Container>
      </Box>

      {/* Values */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.light' }}>
        <Container maxWidth="lg">
          <SectionHeader
            overline="Unsere Werte"
            title="Wofür wir stehen"
            subtitle="Qualität, Zuverlässigkeit und Teamwork — das sind die Grundpfeiler unserer Arbeit."
          />
          <Grid container spacing={3}>
            {values.map((value, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <AnimatedSection delay={i * 0.1}>
                  <Card sx={{ height: '100%', textAlign: 'center', p: 1 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          bgcolor: 'rgba(209,188,138,0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2,
                          color: 'primary.main',
                        }}
                      >
                        <value.icon sx={{ fontSize: 30 }} />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {value.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Timeline */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <SectionHeader
            overline="Geschichte"
            title="Unsere Meilensteine"
          />
          <Stack spacing={0}>
            {milestones.map((m, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 3,
                    py: 3,
                    borderBottom: i < milestones.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: 'primary.main', fontWeight: 800, minWidth: 64 }}
                  >
                    {m.year}
                  </Typography>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                      {m.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {m.desc}
                    </Typography>
                  </Box>
                </Box>
              </AnimatedSection>
            ))}
          </Stack>
        </Container>
      </Box>

      <CTABanner pageKey="about" />
    </>
  );
}
